import { envFlags, FLAGS } from '@thoughtspot/env-flags';
import React, { forwardRef } from 'react';
import {
    isCurrentClusterUrl,
    transformThoughtSpotUrl,
} from '/@services/everywhere/everywhere-service.util';

type LinkProps = React.DetailedHTMLProps<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    HTMLAnchorElement
>;

/**
 * ThoughtSpot-specific custom link override for embed contexts
 * Handles URL transformation and link behavior based on embed configuration
 *
 * This component is registered in embed.container.tsx via setCustomLinkOverride()
 * and automatically applies to all <Link> and <BaseLink> components in the application
 */

/**
 * Extracts the complete URL structure preserving query parameters before the hash
 * Examples:
 * - "/?param=value#/pinboard/123" → "/?param=value#/pinboard/123"
 * - "https://cluster.com/?a=1&b=2#/answer/abc" → "?a=1&b=2#/answer/abc"
 * - "/pinboard/123" → "#/pinboard/123"
 *
 * @param href - The URL to extract from
 * @returns The URL with query params before hash
 */
const extractHashPathFromUrl = (href: string): string => {
    try {
        const url = new URL(href, window.location.origin);

        let hashPath = url.hash || '';
        if (!hashPath) {
            hashPath = url.pathname.startsWith('/')
                ? `#${url.pathname}`
                : `#/${url.pathname}`;
        }

        const result = url.search ? `${url.search}${hashPath}` : hashPath;

        return result;
    } catch (error) {
        // Fallback: normalize the path
        const normalizedPath = href.startsWith('/') ? href : `/${href}`;
        return href.includes('#') ? href : `#${normalizedPath}`;
    }
};

export const EmbedCustomLinkOverride = forwardRef<HTMLAnchorElement, LinkProps>(
    ({ children, ...restProps }, ref) => {
        const updatedProps = { ...restProps };

        const shouldDisableLinks = envFlags.getValue(
            FLAGS.disableRedirectionLinksInNewTab.name,
        );

        if (shouldDisableLinks) {
            delete updatedProps.href;

            if (!updatedProps.onClick && restProps.href) {
                updatedProps.onClick = () => {
                    window.open(restProps.href, '_self');
                };
            }
        } else if (
            updatedProps.href &&
            isCurrentClusterUrl(updatedProps.href)
        ) {
            const hashPath = extractHashPathFromUrl(updatedProps.href);
            updatedProps.href = transformThoughtSpotUrl(hashPath);
        }

        return (
            // eslint-disable-next-line thoughtspot-eslint/restrict-anchor-tags -- [SCAL-283577] Custom link override component for embed contexts with URL transformation and special link behavior
            <a ref={ref} {...updatedProps}>
                {children}
            </a>
        );
    },
);
